import React, { useMemo, useState } from 'react';
import { Card, Table, Row, Col, Statistic, Select, Progress, Tag, Space } from 'antd';
import { BarChartOutlined } from '@ant-design/icons';

const { Option } = Select;

interface RewardItem {
  id: string;
  type: 'image' | 'video' | 'audio';
  url: string;
}

interface RewardItem {
  id: string;
  type: 'image' | 'video' | 'audio';
  url: string;
}

interface BranchOption {
  id: string;
  label: string;
  chooseCount: number;
  affectionDelta: number;
  rewards: RewardItem[];
}

interface BranchGroup {
  id: string;
  name: string;
  triggerCount: number;
  options: BranchOption[];
}

interface ScriptAnalytics {
  id: number;
  title: string;
  roleName: string;
  parentScriptId?: number;
  prerequisiteScriptIds: number[];
  unlockCondition: 'free' | 'vip' | 'paid';
  unlockCost?: number;
  playCount: number;
  completionCount: number;
  branchGroups: BranchGroup[];
}

const mockAnalytics: ScriptAnalytics[] = [
  {
    id: 101,
    title: '雨夜邂逅',
    roleName: '雨夜女王',
    parentScriptId: undefined,
    prerequisiteScriptIds: [],
    unlockCondition: 'free',
    playCount: 520,
    completionCount: 380,
    branchGroups: [
      {
        id: 'g1',
        name: '雨夜初遇',
        triggerCount: 3,
        options: [
          {
            id: 'b1',
            label: '温柔回应',
            chooseCount: 128,
            affectionDelta: 5,
            rewards: [{ id: 'r1', type: 'image', url: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800' }],
          },
          {
            id: 'b2',
            label: '保持距离',
            chooseCount: 64,
            affectionDelta: 1,
            rewards: [],
          },
          {
            id: 'b3',
            label: '邀请留宿',
            chooseCount: 52,
            affectionDelta: 8,
            rewards: [{ id: 'r2', type: 'video', url: 'https://cdn.fantasygf.com/cdn-cgi/media/width=600/https://fantasygf.nyc3.cdn.digitaloceanspaces.com/0f3490b49ccb1ff5e5e58f3b7b67e6af.mp4' }],
          },
        ],
      },
    ],
  },
  {
    id: 102,
    title: '森林小径',
    roleName: '森林旅伴',
    parentScriptId: 101,
    prerequisiteScriptIds: [101],
    unlockCondition: 'vip',
    playCount: 280,
    completionCount: 190,
    branchGroups: [
      {
        id: 'g2',
        name: '森林邀请',
        triggerCount: 5,
        options: [
          {
            id: 'b1',
            label: '答应前往',
            chooseCount: 96,
            affectionDelta: 3,
            rewards: [{ id: 'r1', type: 'video', url: 'https://cdn.fantasygf.com/cdn-cgi/media/width=600/https://fantasygf.nyc3.cdn.digitaloceanspaces.com/58a2b5b8d8d960cd4c92d7118ea02e94.mp4' }],
          },
          {
            id: 'b2',
            label: '询问细节',
            chooseCount: 55,
            affectionDelta: 2,
            rewards: [{ id: 'r2', type: 'audio', url: 'https://example.com/audio/sample.mp3' }],
          },
          {
            id: 'b3',
            label: '礼貌拒绝',
            chooseCount: 18,
            affectionDelta: -1,
            rewards: [],
          },
        ],
      },
    ],
  },
];

const SocialScriptAnalytics: React.FC = () => {
  const [selectedScriptId, setSelectedScriptId] = useState<number>(mockAnalytics[0]?.id || 0);

  const summary = useMemo(() => {
    const totalPlays = mockAnalytics.reduce((sum, item) => sum + item.playCount, 0);
    const totalComplete = mockAnalytics.reduce((sum, item) => sum + item.completionCount, 0);
    const completionRate = totalPlays ? Math.round((totalComplete / totalPlays) * 100) : 0;
    const branchTotal = mockAnalytics.reduce(
      (sum, item) => sum + item.branchGroups.reduce((s, g) => s + g.options.length, 0),
      0
    );
    const rewardTotal = mockAnalytics.reduce(
      (sum, item) => sum + item.branchGroups.reduce((s, g) => s + g.options.reduce((x, b) => x + b.rewards.length, 0), 0),
      0
    );
    return { totalPlays, totalComplete, completionRate, branchTotal, rewardTotal };
  }, []);

  const selectedScript = mockAnalytics.find((item) => item.id === selectedScriptId);
  const branchStats = useMemo(() => {
    if (!selectedScript) return [];
    const flattened = selectedScript.branchGroups.flatMap((group) =>
      group.options.map((option) => ({
        ...option,
        groupName: group.name,
        triggerCount: group.triggerCount,
      }))
    );
    const total = flattened.reduce((sum, item) => sum + item.chooseCount, 0);
    return flattened.map((branch) => ({
      ...branch,
      rate: total ? Math.round((branch.chooseCount / total) * 100) : 0,
    }));
  }, [selectedScript]);

  return (
    <div>
      <Card title="剧本统计" extra={<Tag icon={<BarChartOutlined />}>社交后台</Tag>}>
        <Row gutter={16}>
          <Col span={4}>
            <Statistic title="累计播放" value={summary.totalPlays} />
          </Col>
          <Col span={4}>
            <Statistic title="完成人数" value={summary.totalComplete} />
          </Col>
          <Col span={4}>
            <Statistic title="完成率" value={`${summary.completionRate}%`} />
          </Col>
          <Col span={4}>
            <Statistic title="支线总数" value={summary.branchTotal} />
          </Col>
          <Col span={4}>
            <Statistic title="奖励总数" value={summary.rewardTotal} />
          </Col>
        </Row>
      </Card>

      <Card title="剧本表现" style={{ marginTop: 16 }}>
        <Table
          rowKey="id"
          dataSource={mockAnalytics}
          pagination={false}
          columns={[
            { title: '剧本名称', dataIndex: 'title', key: 'title' },
            { title: '关联角色', dataIndex: 'roleName', key: 'roleName' },
            {
              title: '层级依托',
              key: 'relation',
              render: (_: any, record: ScriptAnalytics) => (
                <Space direction="vertical" size={0}>
                  <span>父剧本：{record.parentScriptId || '-'}</span>
                  <span>前置：{record.prerequisiteScriptIds.length ? record.prerequisiteScriptIds.join(', ') : '无'}</span>
                </Space>
              ),
            },
            {
              title: '解锁条件',
              key: 'unlock',
              render: (_: any, record: ScriptAnalytics) => (
                <Space direction="vertical" size={0}>
                  <Tag color={record.unlockCondition === 'free' ? 'green' : record.unlockCondition === 'vip' ? 'gold' : 'volcano'}>
                    {record.unlockCondition === 'free' ? '免费' : record.unlockCondition === 'vip' ? 'VIP' : '付费'}
                  </Tag>
                  {record.unlockCondition === 'paid' && <span>价格：{record.unlockCost || 0}</span>}
                </Space>
              ),
            },
            {
              title: '分支组/选项',
              key: 'groups',
              render: (_: any, record: ScriptAnalytics) => {
                const optionCount = record.branchGroups.reduce((sum, g) => sum + g.options.length, 0);
                return (
                  <Space>
                    <Tag color="blue">{record.branchGroups.length} 组</Tag>
                    <Tag color="purple">{optionCount} 条</Tag>
                  </Space>
                );
              },
            },
            { title: '播放次数', dataIndex: 'playCount', key: 'playCount' },
            { title: '完成人数', dataIndex: 'completionCount', key: 'completionCount' },
            {
              title: '完成率',
              key: 'rate',
              render: (_: any, record: ScriptAnalytics) => {
                const rate = record.playCount
                  ? Math.round((record.completionCount / record.playCount) * 100)
                  : 0;
                return <Progress percent={rate} size="small" />;
              },
            },
          ]}
        />
      </Card>

      <Card title="支线选择人数" style={{ marginTop: 16 }}>
        <Space style={{ marginBottom: 12 }}>
          <span>选择剧本：</span>
          <Select
            value={selectedScriptId}
            onChange={setSelectedScriptId}
            style={{ width: 200 }}
          >
            {mockAnalytics.map((script) => (
              <Option key={script.id} value={script.id}>
                {script.title}
              </Option>
            ))}
          </Select>
        </Space>
        <Table
          rowKey="id"
          dataSource={branchStats}
          pagination={false}
          columns={[
            { title: '分支组', dataIndex: 'groupName', key: 'groupName' },
            { title: '触发选择数', dataIndex: 'triggerCount', key: 'triggerCount' },
            { title: '支线选项', dataIndex: 'label', key: 'label' },
            { title: '亲密度变化', dataIndex: 'affectionDelta', key: 'affectionDelta' },
            { title: '选择人数', dataIndex: 'chooseCount', key: 'chooseCount' },
            {
              title: '奖励数',
              key: 'rewardCount',
              render: (_: any, record: BranchOption) => (
                <Space>
                  <Tag color="blue">图{record.rewards.filter((r) => r.type === 'image').length}</Tag>
                  <Tag color="purple">视{record.rewards.filter((r) => r.type === 'video').length}</Tag>
                  <Tag color="cyan">音{record.rewards.filter((r) => r.type === 'audio').length}</Tag>
                </Space>
              ),
            },
            {
              title: '占比',
              dataIndex: 'rate',
              key: 'rate',
              render: (rate: number) => <Progress percent={rate} size="small" />,
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default SocialScriptAnalytics;

